// src/Screens/MyRoomsContent.jsx
// Extracted from MyRooms.jsx so the Rooms hub (RoomsHub.jsx) and the
// standalone /my-rooms route can share the same content without duplicating
// its data-fetching/rendering logic. MyRooms.jsx is now a thin wrapper
// around this for the standalone deep link.
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import authService from "../Services/authService";
import accountUpgradeService from "../Services/accountUpgradeService";
import roomCreationService from "../Services/roomCreationService";
import useEnsureDisplayName from "../hooks/useEnsureDisplayName";
import EditNameModal from "../Components/EditNameModal";
import PlayingAsRow from "../Components/PlayingAsRow";

const MyRoomsContent = () => {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accountInfo, setAccountInfo] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const {
    displayName, playerName, draftName, setDraftName, showEditName, setShowEditName, ensureName, syncToAccount,
  } = useEnsureDisplayName();
  const [creatingRoom, setCreatingRoom] = useState(false);
  const [createError, setCreateError] = useState("");

  // Collapsible sections state
  const [activeExpanded, setActiveExpanded] = useState(true);
  const [endedExpanded, setEndedExpanded] = useState(false);
  const [deletedExpanded, setDeletedExpanded] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    loadUserRooms();
    checkAccountStatus();
  }, []);

  const checkAccountStatus = async () => {
    try {
      const info = await accountUpgradeService.getAccountInfo();
      setAccountInfo(info);
    } catch (error) {
      console.error("Error checking account status:", error);
    }
  };

  const loadUserRooms = async () => {
    try {
      setLoading(true);
      const userRooms = await authService.getUserRooms();

      const roomsWithStatus = await Promise.all(
        userRooms.map(async (room) => {
          try {
            const roomQuery = query(
              collection(db, "rooms"),
              where("roomId", "==", room.roomId)
            );
            const roomSnapshot = await getDocs(roomQuery);

            if (!roomSnapshot.empty) {
              const currentRoomData = roomSnapshot.docs[0].data();
              return {
                ...room,
                currentStatus: currentRoomData.gameStatus || "waiting",
                isActive: true,
                isDeleted: false,
                playerCount: currentRoomData.players?.length || 0,
                maxPlayers: currentRoomData.maxPlayers || 3,
                currentRound: currentRoomData.currentRound || 0,
              };
            } else {
              return {
                ...room,
                currentStatus: "deleted",
                isActive: false,
                isDeleted: true,
                playerCount: 0,
                maxPlayers: 3,
                currentRound: 0,
              };
            }
          } catch (error) {
            console.error("Error checking room status:", error);
            return {
              ...room,
              currentStatus: "unknown",
              isActive: false,
              isDeleted: false,
              playerCount: 0,
              maxPlayers: 3,
              currentRound: 0,
            };
          }
        })
      );

      roomsWithStatus.sort(
        (a, b) => new Date(b.joinedAt) - new Date(a.joinedAt)
      );
      setRooms(roomsWithStatus);
    } catch (error) {
      console.error("Error loading user rooms:", error);
    } finally {
      setLoading(false);
    }
  };

  const joinRoom = (roomId) => {
    localStorage.setItem("currentRoomId", roomId);
    navigate("/create-room");
  };

  const handleCreateRoom = async (e) => {
    e.preventDefault();

    try {
      await ensureName();
    } catch (err) {
      setCreateError(err.message);
      return;
    }

    setCreatingRoom(true);
    setCreateError("");

    try {
      const result = await roomCreationService.quickCreateRoom(playerName.trim());

      if (result.success) {
        setShowCreateModal(false);
        setDraftName("");
        syncToAccount();
        navigate("/create-room");
      } else {
        setCreateError(result.message);
      }
    } catch (error) {
      console.error("Room creation error:", error);
      setCreateError("Failed to create room. Please try again.");
    } finally {
      setCreatingRoom(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "waiting":
        return "bg-blue-100 text-blue-800";
      case "starting":
        return "bg-yellow-100 text-yellow-800";
      case "playing":
        return "bg-green-100 text-green-800";
      case "ended":
        return "bg-gray-100 text-gray-800";
      case "deleted":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-600";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "waiting":
        return "Waiting";
      case "starting":
        return "Starting";
      case "playing":
        return "Playing";
      case "ended":
        return "Ended";
      case "deleted":
        return "Deleted";
      default:
        return "Unknown";
    }
  };

  const formatDate = (dateValue) => {
    try {
      let date;

      if (dateValue && typeof dateValue.toDate === "function") {
        date = dateValue.toDate();
      } else if (dateValue instanceof Date) {
        date = dateValue;
      } else {
        date = new Date(dateValue);
      }

      if (isNaN(date.getTime())) {
        return "Unknown date";
      }

      return (
        date.toLocaleDateString() +
        " " +
        date.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        })
      );
    } catch (error) {
      console.error("Error formatting date:", error);
      return "Unknown date";
    }
  };

  // Categorize rooms
  const activeRooms = rooms.filter(
    (room) =>
      !room.isDeleted &&
      (room.currentStatus === "waiting" ||
        room.currentStatus === "starting" ||
        room.currentStatus === "playing")
  );
  const endedRooms = rooms.filter(
    (room) => !room.isDeleted && room.currentStatus === "ended"
  );
  const deletedRooms = rooms.filter((room) => room.isDeleted);

  // Collapsible Section Component
  const CollapsibleSection = ({
    title,
    count,
    isExpanded,
    onToggle,
    children,
    color = "gray",
  }) => (
    <div className="bg-brand-surface rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.04)] border border-brand-border mb-4">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-brand-accentMuted transition-colors"
      >
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-bold text-brand-text">{title}</h2>
          <span
            className={`px-2 py-0.5 rounded-full text-[11px] font-medium bg-${color}-100 text-${color}-800`}
          >
            {count}
          </span>
        </div>
        <svg
          className={`h-4 w-4 text-brand-textSecondary transition-transform ${
            isExpanded ? "rotate-180" : ""
          }`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>
      {isExpanded && <div className="border-t border-brand-borderLight">{children}</div>}
    </div>
  );

  // Active Room Card Component (Box Style)
  const ActiveRoomCard = ({ room }) => (
    <div className="bg-brand-surface border border-brand-border rounded-lg p-4 hover:shadow-[0_1px_3px_rgba(0,0,0,0.06)] transition-shadow">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-bold text-sm text-brand-text truncate">
          {room.roomName}
        </h3>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <span
            className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${getStatusColor(
              room.currentStatus
            )}`}
          >
            {getStatusText(room.currentStatus)}
          </span>
          {room.isHost && (
            <span className="bg-brand-accentMuted text-brand-accent px-2 py-0.5 rounded-full text-[11px] font-medium">
              Host
            </span>
          )}
        </div>
      </div>

      <div className="text-xs text-brand-textSecondary space-y-1.5 mb-3">
        <div className="flex justify-between">
          <span>Players:</span>
          <span className="font-medium text-brand-text">
            {room.playerCount}/{room.maxPlayers}
          </span>
        </div>
        {room.currentRound > 0 && (
          <div className="flex justify-between">
            <span>Round:</span>
            <span className="font-medium text-brand-text">{room.currentRound}</span>
          </div>
        )}
        <div className="flex justify-between">
          <span>Joined:</span>
          <span className="font-medium text-brand-text">{formatDate(room.joinedAt)}</span>
        </div>
      </div>

      <button
        onClick={() => joinRoom(room.roomId)}
        className="w-full border border-brand-accent text-brand-accent py-2 rounded-lg hover:bg-brand-accent hover:text-white transition-colors text-sm font-semibold"
      >
        {room.currentStatus === "waiting" ? "Rejoin Room" : "Continue Game"}
      </button>
    </div>
  );

  // Table Row Component (for ended/deleted rooms)
  const TableRow = ({ room }) => (
    <tr className="border-b border-gray-100 hover:bg-gray-50">
      <td className="py-3 px-4">
        <div className="font-medium text-gray-800">{room.roomName}</div>
      </td>
      <td className="py-3 px-4">
        <span
          className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
            room.currentStatus
          )}`}
        >
          {getStatusText(room.currentStatus)}
        </span>
      </td>
      <td className="py-3 px-4">
        {room.isHost ? (
          <span className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs font-medium">
            Host
          </span>
        ) : (
          <span className="text-gray-500 text-sm">Player</span>
        )}
      </td>
      <td className="py-3 px-4 text-sm text-gray-600">
        {room.playerCount}/{room.maxPlayers}
      </td>
      <td className="py-3 px-4 text-sm text-gray-600">
        {room.currentRound > 0 ? room.currentRound : "-"}
      </td>
      <td className="py-3 px-4 text-sm text-gray-600">
        {formatDate(room.joinedAt)}
      </td>
    </tr>
  );

  return (
    <div className="container mx-auto px-6 py-8">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-lg font-bold text-brand-text">My Rooms</h1>
          <p className="text-brand-textSecondary text-xs">
            See all your game rooms and create new ones
          </p>
        </div>

        {/* The empty state has its own create button; don't show two. */}
        {(loading || rooms.length > 0) && (
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-brand-accent text-white font-semibold px-3.5 py-2 rounded-lg hover:bg-red-700 transition duration-300 flex items-center justify-center text-sm flex-shrink-0"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 mr-1.5"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
              clipRule="evenodd"
            />
          </svg>
          New Room
        </button>
        )}
      </div>

      {/* Account Status Banner */}
      {accountInfo?.type === "anonymous" && (
        <div className="bg-brand-surface border border-brand-border rounded-lg p-4 mb-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h3 className="text-sm font-semibold text-brand-text mb-1">
                💾 Save Your Progress Forever
              </h3>
              <p className="text-xs text-brand-textSecondary">
                Your room history is currently saved for 30 days. Create an
                account to keep it forever and sync across devices!
              </p>
            </div>
            <button
              onClick={() => navigate("/account", { state: { openSignUp: true } })}
              className="bg-brand-accent text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors text-sm font-medium whitespace-nowrap flex-shrink-0"
            >
              Create Account
            </button>
          </div>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="text-center py-10">
          <div className="text-brand-textSecondary text-sm">Loading your rooms...</div>
        </div>
      )}

      {/* No Rooms */}
      {!loading && rooms.length === 0 && (
        <div className="text-center py-10">
          <div className="text-5xl mb-3">🎮</div>
          <p className="text-brand-textSecondary text-sm mb-4">
            Create your first room to start playing!
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-brand-accent text-white px-6 py-2.5 rounded-lg hover:bg-red-700 transition-colors text-sm font-semibold"
          >
            Create Your First Room
          </button>
        </div>
      )}

      {/* Room Sections */}
      {!loading && rooms.length > 0 && (
        <div className="space-y-6">
          {/* Active Rooms - Box Design */}
          {activeRooms.length > 0 && (
            <CollapsibleSection
              title="Active Rooms"
              count={activeRooms.length}
              isExpanded={activeExpanded}
              onToggle={() => setActiveExpanded(!activeExpanded)}
              color="green"
            >
              <div className="p-3">
                <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                  {activeRooms.map((room) => (
                    <ActiveRoomCard key={room.roomId} room={room} />
                  ))}
                </div>
              </div>
            </CollapsibleSection>
          )}

          {/* Ended Rooms - Table Design */}
          {endedRooms.length > 0 && (
            <CollapsibleSection
              title="Ended Games"
              count={endedRooms.length}
              isExpanded={endedExpanded}
              onToggle={() => setEndedExpanded(!endedExpanded)}
              color="gray"
            >
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Room
                      </th>
                      <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Role
                      </th>
                      <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Players
                      </th>
                      <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Round
                      </th>
                      <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Joined
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {endedRooms.map((room) => (
                      <TableRow key={room.roomId} room={room} />
                    ))}
                  </tbody>
                </table>
              </div>
            </CollapsibleSection>
          )}

          {/* Deleted Rooms - Table Design */}
          {deletedRooms.length > 0 && (
            <CollapsibleSection
              title="Deleted Rooms"
              count={deletedRooms.length}
              isExpanded={deletedExpanded}
              onToggle={() => setDeletedExpanded(!deletedExpanded)}
              color="red"
            >
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Room
                      </th>
                      <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Role
                      </th>
                      <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Joined
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {deletedRooms.map((room) => (
                      <tr
                        key={room.roomId}
                        className="border-b border-gray-100 hover:bg-gray-50"
                      >
                        <td className="py-3 px-4">
                          <div className="font-medium text-gray-800">
                            {room.roomName}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          {room.isHost ? (
                            <span className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs font-medium">
                              Host
                            </span>
                          ) : (
                            <span className="text-gray-500 text-sm">
                              Player
                            </span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600">
                          {formatDate(room.joinedAt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CollapsibleSection>
          )}
        </div>
      )}

      {/* Footer Stats */}
      {!loading && rooms.length > 0 && (
        <div className="mt-6 text-center">
          <p className="text-xs text-brand-textSecondary">
            Total: {activeRooms.length} active, {endedRooms.length} ended,{" "}
            {deletedRooms.length} deleted
          </p>
        </div>
      )}

      {/* Create Room Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-800">
                  Create New Room
                </h2>
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setDraftName("");
                    setCreateError("");
                  }}
                  disabled={creatingRoom}
                  className="text-gray-500 hover:text-gray-700 transition-colors p-2 rounded-full hover:bg-gray-100"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              {createError && (
                <div className="bg-red-50 text-red-700 p-3 rounded-lg mb-4 text-sm">
                  {createError}
                </div>
              )}

              <form onSubmit={handleCreateRoom} className="space-y-4">
                {displayName ? (
                  <PlayingAsRow name={displayName} onChange={() => setShowEditName(true)} />
                ) : (
                  <div>
                    <label htmlFor="my-rooms-name" className="block text-sm font-medium text-gray-700 mb-2">
                      Your Name
                    </label>
                    <input
                      id="my-rooms-name"
                      type="text"
                      value={draftName}
                      onChange={(e) => setDraftName(e.target.value)}
                      disabled={creatingRoom}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                      placeholder="Enter your name"
                      required
                    />
                  </div>
                )}

                <button
                  type="submit"
                  disabled={creatingRoom || !playerName.trim()}
                  className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
                    creatingRoom || !playerName.trim()
                      ? "bg-gray-400 text-white cursor-not-allowed"
                      : "bg-red-600 text-white hover:bg-red-700"
                  }`}
                >
                  {creatingRoom ? (
                    <div className="flex items-center justify-center">
                      <svg
                        className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Creating Room...
                    </div>
                  ) : (
                    "Create Room & Play"
                  )}
                </button>
              </form>

              <p className="text-xs text-gray-500 text-center mt-4">
                You'll be the host and can invite friends with a shareable link
              </p>
            </div>
          </div>
        </div>
      )}

      <EditNameModal isOpen={showEditName} onClose={() => setShowEditName(false)} />
    </div>
  );
};

export default MyRoomsContent;
